import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import StoreCard from "./components/StoreCard";
import { api } from "../../hooks/apiClient";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "source", label: "Source" },
  { key: "destination", label: "Destination" },
];

const SkeletonCard = () => (
  <div className="card h-100 border-0 shadow-sm">
    <div className="card-body placeholder-glow">
      <span className="placeholder col-4 mb-3" />
      <span className="placeholder col-8 d-block mb-3" />
      <span className="placeholder col-3" />
    </div>
  </div>
);

const EmptyState = ({ title, text, action }) => (
  <div className="text-center py-5">
    <i className="bi bi-shop fs-1 text-secondary" />
    <h5 className="mt-3">{title}</h5>
    <p className="text-secondary">{text}</p>
    {action}
  </div>
);

const Stores = () => {
  const navigate = useNavigate();

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [snackbar, setSnackbar] = useState(null);

  const notify = (severity, message) => setSnackbar({ severity, message });

  const loadStores = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get("stores");
      setStores(res.data.data);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const setBusy = (id, isBusy) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      isBusy ? next.add(id) : next.delete(id);
      return next;
    });

  // top of the file, temporary mock
  const mockPatch = (shouldFail = false, ms = 1000) =>
    new Promise((resolve, reject) =>
      setTimeout(
        () => (shouldFail ? reject(new Error("mock fail")) : resolve({})),
        ms,
      ),
    );
  const handleToggle = async (store) => {
    const nextValue = !store.isActive;
    setBusy(store.id, true);
    setStores((prev) =>
      prev.map((s) => (s.id === store.id ? { ...s, isActive: nextValue } : s)),
    );
    try {
      await mockPatch(false, 1000);
      // await api.patch(`stores/active/${store.id}`, { isActive: nextValue });

      notify("warning", "Go to store details for active or deactivation");
    } catch (err) {
      console.error(err);
      setStores((prev) =>
        prev.map((s) =>
          s.id === store.id ? { ...s, isActive: !nextValue } : s,
        ),
      );
      notify("error", "Could not update the store. Please try again.");
    } finally {
      setBusy(store.id, false);
    }
  };

  const counts = useMemo(
    () => ({
      all: stores.length,
      source: stores.filter((s) => s.type === "source").length,
      destination: stores.filter((s) => s.type === "destination").length,
      active: stores.filter((s) => s.isActive).length,
    }),
    [stores],
  );

  const visible = useMemo(() => {
    return stores.filter((s) => filter === "all" || s.type === filter);
  }, [stores, filter]);

  const connectButton = (
    <button
      type="button"
      className="btn btn-success"
      onClick={() => navigate("/shopify/connect")}
    >
      <i className="bi bi-plus-lg me-1" /> Connect a Shopify store
    </button>
  );

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h4 className="fw-bold mb-0">Stores</h4>
          {!loading && !error && (
            <small className="text-secondary">
              {counts.active} of {counts.all} active
            </small>
          )}
        </div>
      </div>

      {/* Toolbar */}
      {stores.length > 0 && (
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div className="btn-group" role="group" aria-label="Filter by type">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`btn btn-sm ${
                  filter === f.key ? "btn-dark" : "btn-outline-secondary"
                }`}
                onClick={() => setFilter(f.key)}
              >
                {f.label} <span className="opacity-75">({counts[f.key]})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="row g-3">
          {[1, 2, 3].map((i) => (
            <div className="col-12 col-md-6 col-lg-4" key={i}>
              <SkeletonCard />
            </div>
          ))}
        </div>
      ) : error ? (
        <EmptyState
          title="Couldn't load stores"
          text="Something went wrong while fetching your stores."
          action={
            <button className="btn btn-outline-dark" onClick={loadStores}>
              <i className="bi bi-arrow-clockwise me-1" /> Retry
            </button>
          }
        />
      ) : stores.length === 0 ? (
        <EmptyState
          title="No stores yet"
          text="Connect your first Shopify store to get started."
          action={connectButton}
        />
      ) : visible.length === 0 ? (
        <EmptyState
          title="No matches"
          text="Try a different filter or search term."
        />
      ) : (
        <div className="row g-3">
          {visible.map((store) => (
            <div className="col-12 col-md-12 col-lg-4" key={store.id}>
              <StoreCard
                store={store}
                busy={busyIds.has(store.id)}
                onToggle={handleToggle}
                onOpen={(id) => navigate(`/stores/${id}`)}
              />
            </div>
          ))}
        </div>
      )}

      <Snackbar
        open={!!snackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnackbar(null)}
        autoHideDuration={4000}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.severity}
            variant="filled"
            onClose={() => setSnackbar(null)}
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </div>
  );
};

export default Stores;
